import { NAV_LINKS, BRAND, ASSETS, DISCLAIMER, LICENSING } from '@/lib/brand';
import { Phone, Mail, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export default function Footer() {
  const { pathname } = useLocation();
  const onHome = pathname === '/';

  const scrollTo = (id) => {
    if (!onHome) {
      window.location.href = `/${id}`;
      return;
    }
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#070B12] text-white/80 pt-20 pb-10 border-t border-[#B68A35]/20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10 mb-16">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img src={ASSETS.monogramDark} alt="Bowers monogram" className="h-12 w-12 object-contain rounded-sm ring-1 ring-[#B68A35]/30" />
              <div>
                <p className="font-serif text-white text-xl leading-none">Bowers</p>
                <p className="text-[#D6B36A] text-[0.62rem] tracking-[0.28em] uppercase mt-1.5">Advisory Group</p>
              </div>
            </div>
            <p className="font-serif italic text-[#D6B36A]/85 text-lg mb-4 leading-snug max-w-sm">
              Protect What Matters. Build What's Next.<br />Leave a Lasting Legacy.
            </p>
            <p className="text-white/50 text-sm font-light leading-relaxed max-w-sm">
              A boutique, education-first practice serving families, educators, business owners,
              and faith-based communities.
            </p>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <p className="text-[#D6B36A] text-[0.7rem] tracking-[0.28em] uppercase font-medium mb-5">Explore</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s|&/g, '-')}`}
                    className="text-white/65 hover:text-[#D6B36A] text-sm transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <p className="text-[#D6B36A] text-[0.7rem] tracking-[0.28em] uppercase font-medium mb-5">Contact</p>
            <ul className="space-y-4 text-sm">
              <li>
                <a href={BRAND.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 text-white/70 hover:text-[#D6B36A] transition-colors">
                  <Phone className="w-4 h-4 text-[#B68A35]" strokeWidth={1.5} />
                  {BRAND.phone}
                </a>
              </li>
              <li>
                <a href={BRAND.emailHref} data-testid="footer-email" className="flex items-center gap-3 text-white/70 hover:text-[#D6B36A] transition-colors break-all">
                  <Mail className="w-4 h-4 text-[#B68A35] shrink-0" strokeWidth={1.5} />
                  {BRAND.email}
                </a>
              </li>
              <li>
                <a href={BRAND.websiteHref} target="_blank" rel="noreferrer" data-testid="footer-website" className="flex items-center gap-3 text-white/70 hover:text-[#D6B36A] transition-colors">
                  <Globe className="w-4 h-4 text-[#B68A35]" strokeWidth={1.5} />
                  {BRAND.website}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="gold-divider mb-8" />

        {/* Licensing */}
        <div
          data-testid="footer-licensing"
          className="mb-6 flex flex-wrap items-baseline gap-x-3 gap-y-1"
        >
          <span className="text-[#D6B36A] text-[0.68rem] tracking-[0.28em] uppercase font-medium">Licensed In</span>
          <span className="text-white/70 text-sm tracking-wide">
            {LICENSING.states.join(' · ')}
          </span>
          <span className="text-white/30">·</span>
          <span className="text-white/60 text-sm tracking-wide">
            {LICENSING.residentState} License #{LICENSING.residentLicense}
          </span>
        </div>

        {/* Disclaimer */}
        <p className="text-white/40 text-[0.72rem] leading-relaxed font-light max-w-5xl">
          {DISCLAIMER}
        </p>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-white/40 text-xs tracking-wide">
            &copy; {new Date().getFullYear()} Bowers Advisory Group. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-white/50 hover:text-[#D6B36A] transition-colors tracking-wide"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="text-white/50 hover:text-[#D6B36A] transition-colors tracking-wide"
            >
              Terms of Use
            </Link>
            <span className="text-white/30 text-[0.7rem] tracking-[0.22em] uppercase">
              Boutique · Education-First · Legacy-Centered
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
